import apiService from './api'
import i18n from '../i18n'
import { SUPPORTED_LANGUAGES } from '../i18n/config'

/**
 * User API Service
 * Handles all user-related API endpoints
 */

export const userApi = {
  /**
   * Получение профиля пользователя и начальных данных
   * Returns: { me: {...}, balance: number, tasks: [...], recent_wins: [...], alert?: string, error?: string }
   */
  async getProfile() {
    try {
      const response = await apiService.get('/api/me/')
      return response
    } catch (error) {
      console.error('Ошибка получения профиля пользователя:', error)
      throw error
    }
  },

  /**
   * Нормализация Steam трейдлинка
   * Приводит ссылку к каноническому виду с /?partner= (Steam выдаёт ссылки и с /, и без)
   * @param {string} tradeUrl - Steam трейдлинк пользователя
   * @returns {string} - нормализованный трейдлинк
   */
  normalizeTradeUrl(tradeUrl) {
    if (!tradeUrl || typeof tradeUrl !== 'string') {
      return tradeUrl
    }
    // Если после /new идёт ? без слэша — добавляем слэш
    return tradeUrl.replace(
      'https://steamcommunity.com/tradeoffer/new?partner=',
      'https://steamcommunity.com/tradeoffer/new/?partner='
    )
  },

  /**
   * Валидация Steam трейдлинка
   * @param {string} tradeUrl - Steam трейдлинк пользователя
   * @returns {boolean} - true если валидный, false если нет
   */
  validateTradeUrl(tradeUrl) {
    if (!tradeUrl || typeof tradeUrl !== 'string') {
      return false
    }

    // Проверяем наличие обязательной части в начале ссылки
    return tradeUrl.startsWith('https://steamcommunity.com/tradeoffer/new/?partner=')
  },

  /**
   * Шаг 1: Проверка и установка трейдлинка (без confirm_change)
   * Возвращает полный ответ API, не бросает ошибку на 'not uniq tradelink'
   * @param {string} tradeUrl - Steam трейдлинк пользователя
   * Returns: { tradelink: { result: boolean, charged?: number } } | { error: string, change_cost?: number, balance?: number }
   */
  async updateTradeUrl(tradeUrl) {
    try {
      // Нормализуем ссылку (приводим к виду с /?partner=)
      const normalizedUrl = this.normalizeTradeUrl(tradeUrl)

      // Валидация на клиенте перед отправкой
      if (!this.validateTradeUrl(normalizedUrl)) {
        throw new Error(i18n.t('profile.tradeLinkInvalid'))
      }

      const response = await apiService.postForm('/api/tradelink/', {
        tradelink: normalizedUrl
      })

      // Ошибка формата — бросаем
      if (response.error === 'error format tradelink') {
        throw new Error(i18n.t('profile.tradeLinkInvalid'))
      }

      // not uniq tradelink — возвращаем ответ, не бросаем (нужно для UI подтверждения)
      // not enough crystals — не бывает на шаге 1, но обрабатываем на всякий случай
      return response
    } catch (error) {
      console.error('Ошибка при обновлении трейдлинка (шаг 1):', error)
      throw error
    }
  },

  /**
   * Шаг 2: Подтверждение платной установки трейдлинка (с confirm_change: 1)
   * @param {string} tradeUrl - Steam трейдлинк пользователя
   * Returns: { tradelink: { result: boolean, charged?: number } } | { error: string, change_cost?: number, balance?: number }
   */
  async confirmTradeUrl(tradeUrl) {
    try {
      // Нормализуем ссылку (приводим к виду с /?partner=)
      const normalizedUrl = this.normalizeTradeUrl(tradeUrl)

      const response = await apiService.postForm('/api/tradelink/', {
        tradelink: normalizedUrl,
        confirm_change: 1
      })

      return response
    } catch (error) {
      console.error('Ошибка при подтверждении трейдлинка (шаг 2):', error)
      throw error
    }
  },

  /**
   * Список поддерживаемых языков приложения
   * Единый реестр кодов находится в src/i18n/config.js
   */
  SUPPORTED_LANGUAGES: SUPPORTED_LANGUAGES,

  /**
   * Валидация языка
   * @param {string} language - Код языка (ru, en, etc.)
   * @returns {boolean} - true если язык поддерживается, false если нет
   */
  validateLanguage(language) {
    if (!language || typeof language !== 'string') {
      return false
    }
    
    return this.SUPPORTED_LANGUAGES.includes(language.toLowerCase())
  },

  /**
   * Обновление языка пользователя
   * @param {string} language - Код языка (ru, en, etc.)
   * Returns: { lang: { result: boolean }, me: {...}, alert?: string, error?: string }
   */
  async updateLanguage(language) {
    try {
      // Валидация языка на клиенте перед отправкой
      if (!this.validateLanguage(language)) {
        throw new Error(`Неподдерживаемый язык. Доступные языки: ${this.SUPPORTED_LANGUAGES.join(', ')}`)
      }

      const response = await apiService.postForm('/api/lang/', {
        lang: language.toLowerCase()
      })
      
      // Проверяем результат от сервера
      if (response.lang && !response.lang.result) {
        throw new Error('Не удалось изменить язык на сервере')
      }
      
      return response
    } catch (error) {
      console.error('Ошибка при обновлении языка:', error)
      throw error
    }
  },

  /**
   * Получение списка доступных валют
   * Returns: [{ code: string, string: string, select?: boolean }, ...]
   */
  async getCurrencies() {
    try {
      const response = await apiService.get('/api/currency/')

      if (!Array.isArray(response)) {
        throw new Error('Некорректный формат списка валют')
      }

      return response
    } catch (error) {
      console.error('Ошибка при получении списка валют:', error)
      throw error
    }
  },

  /**
   * Валидация кода валюты
   * @param {string} currencyCode - Трехбуквенный код валюты
   * @returns {boolean} - true если код валиден
   */
  validateCurrencyCode(currencyCode) {
    if (!currencyCode || typeof currencyCode !== 'string') {
      return false
    }

    return /^[A-Z]{3}$/.test(currencyCode.trim().toUpperCase())
  },

  /**
   * Установка валюты пользователя
   * @param {string} currencyCode - Трехбуквенный код валюты
   * Returns: { status: 'OK', alert?: string, error?: string }
   */
  async updateCurrency(currencyCode) {
    try {
      const normalizedCode = currencyCode?.trim()?.toUpperCase()

      if (!this.validateCurrencyCode(normalizedCode)) {
        throw new Error('Некорректный код валюты')
      }

      const response = await apiService.post('/api/currency/', {
        code: normalizedCode
      })

      if (response?.status !== 'OK' && !response?.alert) {
        throw new Error(response?.error || 'Не удалось изменить валюту')
      }

      return response
    } catch (error) {
      console.error('Ошибка при обновлении валюты:', error)
      throw error
    }
  },

  /**
   * Complete a task
   * @param {number} taskId - The ID of the task to complete
   * Returns: { me: {...}, balance: number, tasks: [...], alert?: string, error?: string }
   */
  async completeTask(taskId) {
    try {
      const response = await apiService.post(`/api/tasks/${taskId}/`)
      return response
    } catch (error) {
      console.error(`Failed to complete task ${taskId}:`, error)
      throw error
    }
  },

  /**
   * Get transaction history
   * @param {number} page - Page number (optional)
   * @param {number} limit - Items per page (optional)
   * Returns: { transactions: [...], pagination: {...}, alert?: string, error?: string }
   */
  async getTransactionHistory(page = 1, limit = 20) {
    try {
      const response = await apiService.get('/api/user/transactions/', { page, limit })
      return response
    } catch (error) {
      console.error('Failed to fetch transaction history:', error)
      throw error
    }
  },

  /**
   * Get recent wins (for homepage)
   * Returns: { recent_wins: [...], alert?: string, error?: string }
   */
  async getRecentWins() {
    try {
      const response = await apiService.get('/api/user/recent-wins/')
      return response
    } catch (error) {
      console.error('Failed to fetch recent wins:', error)
      throw error
    }
  },

  /**
   * Initialize user session (called on app start)
   * Returns: { me: {...}, balance: number, cases: [...], tasks: [...], recent_wins: [...], alert?: string, error?: string }
   */
  async initializeSession() {
    try {
      const response = await apiService.get('/api/user/init/')
      return response
    } catch (error) {
      console.error('Failed to initialize user session:', error)
      throw error
    }
  },

  /**
   * Создание PreparedInlineMessage для отправки приглашения
   * @param {string} inviteText - текст приглашения
   * @param {string} refLink - реферальная ссылка
   * Returns: { prepared_message: { id: string, expiration_date: number }, alert?: string, error?: string }
   */
  async createPreparedInviteMessage(inviteText, refLink) {
    try {
      const fullMessage = `${inviteText}\n\n${refLink}`
      
      const response = await apiService.post('/api/inviteFriend/', {
        message_text: fullMessage,
        ref_link: refLink,
        // Разрешаем отправку во все типы чатов
        allow_user_chats: true,
        allow_bot_chats: false,
        allow_group_chats: true,
        allow_channel_chats: false
      })
      
      return response
    } catch (error) {
      console.error('Failed to create prepared invite message:', error)
      throw error
    }
  },

  /**
   * Получение статистики рефералов
   * Returns: { 
   *   all_ref_count: number, 
   *   level_1_count: number, 
   *   level_2_count: number,
   *   stat: { level_1_today, level_1_week, level_1_month, level_1_lifetime, ... },
   *   ref: [{ chat_id, user_info, subref_count, stat }],
   *   alert?: string, 
   *   error?: string 
   * }
   */
  async getReferralStats() {
    try {
      const response = await apiService.get('/api/referral/')
      return response
    } catch (error) {
      console.error('Ошибка получения статистики рефералов:', error)
      throw error
    }
  },

  /**
   * Получение топа партнеров (топ-10 по заработку за разные периоды)
   * Returns: {
   *   referral_top: {
   *     today: { 1-10: { earn, all_ref_count, user_info: { name, ava } } },
   *     week: { 1-10: { earn, all_ref_count, user_info: { name, ava } } },
   *     month: { 1-10: { earn, all_ref_count, user_info: { name, ava } } },
   *     position_in_top_today: number,
   *     position_in_top_week: number,
   *     position_in_top_month: number
   *   },
   *   alert?: string,
   *   error?: string
   * }
   */
  async getReferralTop() {
    try {
      const response = await apiService.get('/api/referral/top/')
      return response
    } catch (error) {
      console.error('Ошибка получения топа партнеров:', error)
      throw error
    }
  },

  /**
   * Обновление состояния звука (вкл/выкл)
   * @param {boolean} enabled - true = включить, false = выключить
   * Returns: { sound: { result: boolean, enabled: boolean } } | { error: string }
   */
  async updateSound(enabled) {
    try {
      const response = await apiService.postForm('/api/sound/', {
        sound: enabled ? '1' : '0'
      })

      return response
    } catch (error) {
      console.error('Ошибка при обновлении состояния звука:', error)
      throw error
    }
  }
}

export default userApi